import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FC, useState } from 'react';
import {
  Control,
  FieldArrayWithId,
  UseFieldArrayMove,
  UseFieldArrayRemove,
  useWatch,
} from 'react-hook-form';
import styled from 'styled-components';

import Text from '@/components/Text';

import { FormDataT } from '..';
import Card from './Card';
import SortableTip from './SortableTip';

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Count = styled(Text)`
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.lightGray};
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface Props {
  control: Control<FormDataT>;
  remove: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  fields: FieldArrayWithId<FormDataT, 'parts', 'id'>[];
}

const RecipeCard: FC<Props> = ({ control, remove, move, fields }) => {
  const parts = useWatch({ control, name: 'parts' });
  const sensors = useSensors(useSensor(PointerSensor));
  const [activePartIndex, setActivePartIndex] = useState<number>(-1);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActivePartIndex(-1);

    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((part) => part.id === active.id);
      const newIndex = fields.findIndex((part) => part.id === over?.id);

      move(oldIndex, newIndex);
    }
  };

  const onDragStart = (event: DragStartEvent) => {
    setActivePartIndex(fields.findIndex((part) => part.id === event.active.id));
  };

  return (
    <Card
      header={
        parts.length ? (
          <Header>
            <Heading>
              <Text $type="smallBold" as="span">
                Files
              </Text>
              <Count $type="smallHeadline" as="span">
                {parts.length}
              </Count>
            </Heading>
            <Text
              $type="smallBold"
              $color="error"
              as="button"
              onClick={() => remove()}
            >
              Clear selection
            </Text>
          </Header>
        ) : null
      }
    >
      {parts.length ? (
        <List>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
          >
            <SortableContext
              items={fields}
              strategy={verticalListSortingStrategy}
            >
              {fields.map((part, i) => (
                <SortableTip
                  key={part.id}
                  part={part}
                  remove={remove}
                  index={i}
                />
              ))}
            </SortableContext>
            <DragOverlay>
              {activePartIndex !== -1 && (
                <SortableTip
                  part={fields[activePartIndex]}
                  remove={remove}
                  index={activePartIndex}
                />
              )}
            </DragOverlay>
          </DndContext>
        </List>
      ) : (
        <Center>
          <Text $type="button">No selected files</Text>
        </Center>
      )}
    </Card>
  );
};

export default RecipeCard;
